/**
 * Admin god-mode schemas.
 *
 * Covers the `/admin/*` endpoint surface exposed to super_admin users:
 * tenant lifecycle (suspend/resume/delete/export/plan), migrations, audit
 * log, impersonation, backups, bulk ops.
 *
 * Conventions:
 *   - Every write endpoint accepts an optional `Idempotency-Key` header.
 *     Captured at the handler boundary — not part of the body.
 *   - Every destructive action (suspend/delete/plan-change) REQUIRES a
 *     `reason` field in the request body for audit traceability.
 */
import { z } from 'zod';
import { ISO8601Date, TenantId, UserId } from '../primitives/index.js';
// ============================================================================
// COMMON
// ============================================================================
/** Opaque idempotency key — max 128 chars (matches DB column). */
export const IdempotencyKey = z.string().min(1).max(128);
/** Non-empty free-text reason required by destructive actions. */
export const AdminReason = z.string().min(3).max(1000);
export const AuditStatus = z.enum(['pending', 'succeeded', 'failed', 'reverted']);
// ============================================================================
// AUDIT LOG
// ============================================================================
export const AuditLogEntrySchema = z.looseObject({
    id: z.uuid(),
    actorId: UserId,
    actorEmail: z.email(),
    action: z.string().min(1),
    targetType: z.string().min(1),
    targetId: z.string().min(1),
    tenantId: TenantId.nullable(),
    requestId: z.string().nullable(),
    idempotencyKey: IdempotencyKey.nullable(),
    reason: z.string().nullable(),
    beforeState: z.unknown().nullable(),
    afterState: z.unknown().nullable(),
    status: AuditStatus,
    error: z.string().nullable(),
    createdAt: ISO8601Date,
    updatedAt: ISO8601Date,
});
export const ListAuditLogResponseSchema = z.object({
    entries: z.array(AuditLogEntrySchema),
    count: z.number().int().nonnegative(),
    nextCursor: z.string().nullable().optional(),
});
// ============================================================================
// TENANT LIFECYCLE
// ============================================================================
export const SuspendTenantRequestSchema = z.object({
    reason: AdminReason,
});
export const ResumeTenantRequestSchema = z.object({
    reason: AdminReason,
});
export const SuspendTenantResponseSchema = z.object({
    message: z.string().min(1),
    tenant: z.object({
        id: TenantId,
        status: z.literal('suspended'),
        suspendedAt: ISO8601Date,
    }),
});
export const ResumeTenantResponseSchema = z.object({
    message: z.string().min(1),
    tenant: z.object({
        id: TenantId,
        status: z.literal('active'),
        resumedAt: ISO8601Date,
    }),
});
// ============================================================================
// TENANT DELETION (2-step: plan → execute)
// ============================================================================
/**
 * L'ordine di questo enum è l'ordine di esecuzione della saga.
 *
 * `deleteEtlConfigs` e `deleteMasterRows` sono stati aggiunti dopo aver
 * scoperto che la saga non cancellava la riga `tenants`: senza quella, le FK
 * ON DELETE CASCADE (connectors → jobs → job_runs, user_invitations) non
 * scattano mai e il tenant lascia dietro di sé metadati orfani per sempre.
 * `deleteMasterRows` deve restare DOPO `dropMysqlSchema`, perché il drop
 * risolve il nome dello schema da `tenants.schema_name` e fallirebbe se la
 * riga fosse già sparita.
 */
export const TenantDeletePlanStepSchema = z.enum([
    'cancelSchedulers',
    'disableCognitoUsers',
    'markUsersDeleted',
    'deleteEtlConfigs',
    'deleteDdbItem',
    'dropMysqlSchema',
    'deleteCognitoUsers',
    'deleteMasterRows',
    'deleteS3Prefix',
]);
export const TenantBlastRadiusSchema = z.object({
    users: z.number().int().nonnegative(),
    jobs: z.number().int().nonnegative(),
    connectors: z.number().int().nonnegative(),
    mysqlMb: z.number().nonnegative(),
    ddbItems: z.number().int().nonnegative(),
    s3Bytes: z.number().nonnegative(),
});
export const CreateDeletePlanRequestSchema = z.object({
    reason: AdminReason,
});
export const CreateDeletePlanResponseSchema = z.object({
    planId: z.uuid(),
    tenantId: TenantId,
    blastRadius: TenantBlastRadiusSchema,
    expiresAt: ISO8601Date,
});
export const ExecuteDeletePlanResponseSchema = z.object({
    planId: z.uuid(),
    tenantId: TenantId,
    steps: z.record(TenantDeletePlanStepSchema, z.object({
        status: z.enum(['pending', 'done', 'failed', 'skipped']),
        error: z.string().optional(),
    })),
    overallStatus: z.enum(['in_progress', 'succeeded', 'failed', 'partial']),
});
// ============================================================================
// PLAN CHANGE
// ============================================================================
export const PlanName = z.enum(['starter', 'professional', 'business', 'enterprise']);
export const ChangeTenantPlanRequestSchema = z.object({
    plan: PlanName,
    reason: AdminReason,
    /** When true, ignore downgrade limit violations and proceed. Audit logs it. */
    force: z.boolean().optional(),
});
export const ChangeTenantPlanResponseSchema = z.object({
    message: z.string().min(1),
    tenant: z.object({
        id: TenantId,
        plan: PlanName,
        updatedAt: ISO8601Date,
    }),
});
// ============================================================================
// IMPERSONATION
// ============================================================================
export const ImpersonateTenantRequestSchema = z.object({
    reason: AdminReason,
});
export const ImpersonateTenantResponseSchema = z.object({
    accessToken: z.string().min(1),
    expiresAt: ISO8601Date,
    tenantId: TenantId,
});
// ============================================================================
// GDPR EXPORT (5.3)
// ============================================================================
export const ExportTenantGdprRequestSchema = z.object({
    reason: AdminReason,
});
export const ExportTenantGdprResponseSchema = z.object({
    tenantId: TenantId,
    s3Key: z.string().min(1),
    bytes: z.number().int().nonnegative(),
    expiresAt: ISO8601Date,
    downloadUrl: z.string().min(1),
});
// ============================================================================
// BACKUP & RESTORE (5.4)
// ============================================================================
export const BackupId = z.uuid().brand();
export const TenantBackupSchema = z.object({
    id: BackupId,
    tenantId: TenantId,
    s3Key: z.string().min(1),
    sizeBytes: z.number().int().nonnegative(),
    createdAt: ISO8601Date,
    createdBy: UserId,
});
export const CreateTenantBackupRequestSchema = z.object({
    reason: AdminReason,
});
export const CreateTenantBackupResponseSchema = z.object({
    backup: TenantBackupSchema,
});
export const ListTenantBackupsResponseSchema = z.object({
    backups: z.array(TenantBackupSchema),
    count: z.number().int().nonnegative(),
});
export const RestoreBackupAsNewRequestSchema = z.object({
    reason: AdminReason,
    /** Optional override slug; defaults to `<source-slug>-restored-YYYYMMDD`. */
    slug: z.string().min(1).max(64).optional(),
    /** Optional override name; defaults to `<source-name> (restored YYYY-MM-DD)`. */
    name: z.string().min(1).max(255).optional(),
});
export const RestoreBackupAsNewResponseSchema = z.object({
    sourceTenantId: TenantId,
    sourceBackupId: BackupId,
    newTenantId: TenantId,
    slug: z.string(),
    name: z.string(),
    createdAt: ISO8601Date,
});
// ============================================================================
// SCHEMA MIGRATIONS (2.1 — master DB)
// ============================================================================
/** Zero-padded 4-digit version prefix (e.g. "0001"). */
export const MigrationVersion = z
    .string()
    .regex(/^\d{4}$/, 'Migration version must be a 4-digit zero-padded string')
    .brand();
export const MigrationChecksum = z
    .string()
    .regex(/^[0-9a-f]{64}$/, 'Checksum must be a lowercase hex SHA-256')
    .brand();
/** Pending migration on disk (not yet applied). */
export const PendingMigrationSchema = z.object({
    version: MigrationVersion,
    slug: z.string().min(1),
    checksum: MigrationChecksum,
    hasDownSql: z.boolean(),
});
/** Applied migration row (from master.schema_migrations). */
export const AppliedMigrationSchema = z.object({
    version: MigrationVersion,
    checksum: MigrationChecksum,
    appliedAt: ISO8601Date,
    appliedBy: z.string().nullable(),
});
export const ListMigrationsResponseSchema = z.object({
    applied: z.array(AppliedMigrationSchema),
    pending: z.array(PendingMigrationSchema),
});
export const ApplyMigrationRequestSchema = z.object({
    version: MigrationVersion,
    dryRun: z.boolean().optional(),
    reason: AdminReason,
});
export const ApplyMigrationResponseSchema = z.object({
    version: MigrationVersion,
    checksum: MigrationChecksum,
    status: z.enum(['applied', 'dry-run', 'already-applied']),
    appliedAt: ISO8601Date.nullable(),
});
export const RevertMigrationRequestSchema = z.object({
    version: MigrationVersion,
    reason: AdminReason,
});
export const RevertMigrationResponseSchema = z.object({
    version: MigrationVersion,
    status: z.enum(['reverted', 'not-applied']),
});
// ============================================================================
// BROADCAST EMAIL (5.3)
// ============================================================================
/** Segment filter — which tenants receive the message. */
export const BroadcastSegmentSchema = z.object({
    /** When 'all', ignore plan/status filters. */
    kind: z.enum(['all', 'by_plan', 'by_status']),
    plan: PlanName.optional(),
    status: z.enum(['trial', 'active', 'suspended', 'cancelled']).optional(),
});
export const BroadcastRequestSchema = z.object({
    segment: BroadcastSegmentSchema,
    subject: z.string().min(1).max(200),
    /** Markdown body, 1-50k chars. Converted to plain text for SES. */
    body: z.string().min(1).max(50_000),
    reason: AdminReason,
    /** When true, only super_admin recipients are tested (safe dry-run). */
    dryRun: z.boolean().optional(),
});
export const BroadcastResponseSchema = z.object({
    recipientsCount: z.number().int().nonnegative(),
    sentCount: z.number().int().nonnegative(),
    failedCount: z.number().int().nonnegative(),
    dryRun: z.boolean(),
});
// ============================================================================
// BULK PLAN CHANGE (5.3)
// ============================================================================
export const BulkPlanChangeRequestSchema = z.object({
    fromPlan: PlanName,
    toPlan: PlanName,
    reason: AdminReason,
    /** When true, overrides the per-tenant downgrade guard. */
    force: z.boolean().optional(),
});
export const BulkPlanChangeResultSchema = z.object({
    tenantId: TenantId,
    slug: z.string(),
    status: z.enum(['changed', 'skipped', 'failed']),
    error: z.string().nullable().optional(),
});
export const BulkPlanChangeResponseSchema = z.object({
    processedCount: z.number().int().nonnegative(),
    changedCount: z.number().int().nonnegative(),
    skippedCount: z.number().int().nonnegative(),
    failedCount: z.number().int().nonnegative(),
    results: z.array(BulkPlanChangeResultSchema),
});
//# sourceMappingURL=index.js.map